/**
 * fraudPointFollowUp — the REACTIVE Moderate-band (FraudPoint 500–749)
 * follow-up checklist (Bill 2026-06-12: "the risk language should reflect that
 * the suggested actions have been satisfied").
 *
 * The legacy procedure (Online Applicants.pdf pp.2-3; transcribed in
 * docs/operations/person-modal-inventory/2026-06-03-smoke-test-findings.md §D2;
 * memory project_fraudpoint_gating_d2_procedure_20260603) says a Moderate score
 * requires FOLLOW-UP before continuing. Previously the modal printed that rule
 * as a wall of static text that never reflected WHICH required actions were
 * already captured, and the Verify rollup showed the AML/fraud section empty
 * even after the items were satisfied.
 *
 * This pure module turns that rule into a LIVE checklist: each required action
 * is a row with a `satisfied` flag computed from the person's CURRENT captured
 * state. It reuses the existing signal helpers — NOT new sources:
 *
 *   - government photo ID  → CIP `extra.idType`/`idType2` slot-machine codes
 *     (Core Director F254; '5' = SSN card) + `readAttachedDocs` photo-ID
 *     docCodes — the SAME signals the BE FraudPointRiskGateService reads
 *     (PhotoIdDocCodes / SsnCardDocCodes / idType parse), mirrored so the FSR
 *     preview matches the commit gate.
 *   - address verification → `deriveAddressCorroboration` (the relocated
 *     Address Verification tile's "corroborated yet?" state — satisfied when a
 *     captured doc corroborates the stated address OR LexisNexis verified it,
 *     which `deriveLnContactClearance` already records on the address section).
 *   - SSN card             → SSN_CARD attached doc / `idType`=='5' (only on the
 *     SSN-differs branch).
 *   - in-/out-market       → `person.state` (MO = in-market; else out-of-market)
 *     mirroring FraudPointRiskGateService.IsOutOfMarket EXACTLY.
 *
 * NOTE (out of scope): precise Phelps/Greene + bordering-county classification
 * is DEFERRED to Bill's authoritative list. In-state MO = in-market here; only
 * out-of-STATE is treated as out-of-market (matches the BE enforcement signal).
 *
 * Pure — call per render. No React imports; unit-testable.
 */

import type { RaoPerson } from '../../../types';
import { readAttachedDocs } from '../shared/attachedDocs';
import {
  deriveAddressCorroboration,
  type AddressCorroboration,
} from './addressCorroboration';

/** FraudPoint band thresholds — procedure-locked, mirror the BE constants. */
export const FP_LOW_FLOOR = 750; // >= 750 → Low (proceed)
export const FP_MODERATE_FLOOR = 500; // 500–749 → Moderate (follow-up)
// < 500 → High (decline)

export type FraudPointBand = 'Low' | 'Moderate' | 'High';

/** Classify a FraudPoint score into its procedure band. */
export function classifyFraudPointBand(score: number): FraudPointBand {
  return score >= FP_LOW_FLOOR ? 'Low' : score >= FP_MODERATE_FLOOR ? 'Moderate' : 'High';
}

/**
 * Out-of-market = out-of-state. In-state MO is in-market (precise county
 * classification deferred). Mirrors FraudPointRiskGateService.IsOutOfMarket:
 * blank state defaults to in-market.
 */
export function isOutOfMarket(state: string | undefined | null): boolean {
  const s = (state ?? '').trim();
  return s.length > 0 && s.toUpperCase() !== 'MO';
}

// ── Doc-code sets — mirror FraudPointRiskGateService (case-insensitive) ──────
// Government PHOTO ID docCodes / tileIds.
const PHOTO_ID_DOC_CODES = new Set(
  [
    'GOVT_ID', 'GOVT_ID_GUARDIAN', 'PASSPORT', 'GREEN_CARD',
    'primary-id', 'secondary-id', 'drivers-license', 'foreign-passport', 'green-card',
  ].map((c) => c.toLowerCase()),
);
// SSN card docCodes / tileIds.
const SSN_CARD_DOC_CODES = new Set(['SSN_CARD', 'ssn-card'].map((c) => c.toLowerCase()));

/** CIP IdVerifyCode (Core Director F254) for the SSN card — code '5'. */
const SSN_CARD_ID_CODE = '5';

function readExtraStr(person: RaoPerson, key: string): string {
  const v = (person.extra as Record<string, unknown> | undefined)?.[key];
  return typeof v === 'string' ? v.trim() : '';
}

/**
 * Government photo ID captured? Mirrors the BE: an `idType` slot-machine code
 * that is NOT the SSN-card code (so a real photo ID was slotted), OR a
 * photo-ID docCode in attachedDocs.
 */
export function hasPhotoIdCaptured(person: RaoPerson): boolean {
  const idType = readExtraStr(person, 'idType');
  if (idType !== '' && idType !== SSN_CARD_ID_CODE) return true;
  return readAttachedDocs(person).some((d) => PHOTO_ID_DOC_CODES.has(d.docCode.toLowerCase()));
}

/**
 * SSN card captured? Mirrors the BE: `idType`/`idType2` == '5', OR an SSN_CARD
 * docCode in attachedDocs.
 */
export function hasSsnCardCaptured(person: RaoPerson): boolean {
  const idType = readExtraStr(person, 'idType');
  const idType2 = readExtraStr(person, 'idType2');
  if (idType === SSN_CARD_ID_CODE || idType2 === SSN_CARD_ID_CODE) return true;
  return readAttachedDocs(person).some((d) => SSN_CARD_DOC_CODES.has(d.docCode.toLowerCase()));
}

/** One required follow-up action, with its live satisfied/unmet state. */
export interface FollowUpItem {
  /** Stable key for React + tooltip association. */
  readonly key: 'photoId' | 'addressVerification' | 'ssnCard';
  /** Short row label. */
  readonly label: string;
  /** True when this action is satisfied by the person's current captured state. */
  readonly satisfied: boolean;
  /**
   * Whether the item is unconditionally required for this market, or conditional
   * (only required on an address/SSN discrepancy). Conditional-unmet items do
   * NOT block the rollup — they surface as advisory.
   */
  readonly requirement: 'required' | 'conditional';
  /** Verbatim procedure "why" text — kept intact for the tooltip / legal trail. */
  readonly why: string;
  /** Short human note about the live state (e.g. "matches stated address"). */
  readonly detail?: string;
}

export interface FraudPointFollowUp {
  readonly band: FraudPointBand;
  readonly outOfMarket: boolean;
  /** The market-correct required-set rows, each with live satisfied state. */
  readonly items: ReadonlyArray<FollowUpItem>;
  /** True when every `required` row is satisfied (conditionals never block). */
  readonly allRequiredSatisfied: boolean;
}

// Verbatim collect-rule "why" strings (legal language — DO NOT reword).
const WHY_IN_MARKET =
  'In-market (Phelps / Greene + bordering counties): government photo ID REQUIRED + address verification (if the photo-ID address ≠ application AND not LexisNexis-verifiable) OR SSN card (if SSN differs). Out-of-county MO = use the out-of-market rule.';
const WHY_OUT_OF_MARKET =
  'Out-of-market / out-of-state: government photo ID REQUIRED + SSN card REQUIRED + address verification (not required if the photo-ID address matches the application OR LexisNexis verifies the address).';

/**
 * Build the Moderate-band follow-up checklist for a person. Returns `null` when
 * the band is NOT Moderate (Low = proceed, High = decline — no checklist).
 */
export function deriveFraudPointFollowUp(person: RaoPerson): FraudPointFollowUp | null {
  const score = person.amlInsightScore;
  if (typeof score !== 'number' || Number.isNaN(score)) return null;
  const band = classifyFraudPointBand(score);
  if (band !== 'Moderate') return null;

  const outOfMarket = isOutOfMarket(person.state);
  const photoId = hasPhotoIdCaptured(person);
  const ssnCard = hasSsnCardCaptured(person);
  const corro: AddressCorroboration = deriveAddressCorroboration(person);
  // Address is satisfied when a captured doc corroborates the stated address
  // (deriveAddressCorroboration) OR LexisNexis verified it — the latter lands on
  // the address Verify section as pass/clear-via-comp-doc via
  // deriveLnContactClearance; reflect that here so an LN-verified address counts.
  const addressVerified = corro.corroborated || isAddressLnVerified(person);

  const items: FollowUpItem[] = [];
  const why = outOfMarket ? WHY_OUT_OF_MARKET : WHY_IN_MARKET;

  // Government photo ID — REQUIRED in both markets.
  items.push({
    key: 'photoId',
    label: 'Government photo ID captured',
    satisfied: photoId,
    requirement: 'required',
    why,
    detail: photoId ? 'A government photo ID is on file.' : 'No government photo ID captured yet.',
  });

  if (outOfMarket) {
    // Out-of-market: SSN card REQUIRED; address verification CONDITIONAL
    // (not required if the photo-ID address matches the application OR LN
    // verifies the address).
    items.push({
      key: 'ssnCard',
      label: 'SSN card captured',
      satisfied: ssnCard,
      requirement: 'required',
      why,
      detail: ssnCard ? 'An SSN card is on file.' : 'No SSN card captured yet.',
    });
    items.push({
      key: 'addressVerification',
      label: 'Address verification',
      satisfied: addressVerified,
      requirement: 'conditional',
      why,
      detail: addressDetail(addressVerified, corro),
    });
  } else {
    // In-market: government photo ID REQUIRED; THEN address verification (if the
    // photo-ID address ≠ application AND not LexisNexis-verifiable) OR SSN card
    // (if SSN differs) — both CONDITIONAL on a discrepancy.
    items.push({
      key: 'addressVerification',
      label: 'Address verification (if photo-ID address ≠ application & not LexisNexis-verifiable)',
      satisfied: addressVerified,
      requirement: 'conditional',
      why,
      detail: addressDetail(addressVerified, corro),
    });
    items.push({
      key: 'ssnCard',
      label: 'SSN card (if SSN differs)',
      satisfied: ssnCard,
      requirement: 'conditional',
      why,
      detail: ssnCard ? 'An SSN card is on file.' : 'Capture only if the stated SSN differs from the SSN on file.',
    });
  }

  const allRequiredSatisfied = items
    .filter((i) => i.requirement === 'required')
    .every((i) => i.satisfied);

  return { band, outOfMarket, items, allRequiredSatisfied };
}

function addressDetail(verified: boolean, corro: AddressCorroboration): string {
  if (verified) {
    return corro.corroboratedBy
      ? `Corroborated by the captured ${corro.corroboratedBy}.`
      : 'Address verified (LexisNexis or a captured proof document).';
  }
  if (corro.idAddressConflict) {
    return `The captured ${corro.idAddressConflict.docLabel}'s address differs from the stated address (${corro.idAddressConflict.detail}).`;
  }
  if (corro.noStatedAddress) return 'No stated address entered yet.';
  return 'No captured document confirms the stated physical address yet.';
}

/**
 * True when the LexisNexis report verified the address — recorded on the
 * `address` Verify section as `pass` / `clear-via-comp-doc` by
 * deriveLnContactClearance after an LN import. Reads the persisted clearance
 * map so an LN-verified address satisfies the conditional address item even
 * with no separate proof doc captured.
 */
function isAddressLnVerified(person: RaoPerson): boolean {
  const map = (person.extra as { verifyClearance?: Record<string, { state?: string }> } | undefined)
    ?.verifyClearance;
  const addr = map?.address;
  return !!addr && (addr.state === 'pass' || addr.state === 'clear-via-comp-doc');
}
